import { rowsHaveColumn } from './columnGuards';
import { toFiniteSeriesNumber } from './toFiniteSeriesNumber';
import type {
  ComputedColumnsConfig,
  StepApplyResult,
  TransformRow,
} from './types';

function finiteNumber(value: unknown): number | undefined {
  const parsed = toFiniteSeriesNumber(value);
  return parsed === null ? undefined : parsed;
}

/**
 * Adds declared columns to each row. `pct_change` is
 * `(numerator - denominator) / denominator`.
 *
 * A missing operand column skips the step and leaves rows unchanged. A zero
 * denominator, or a non-numeric operand, writes `null` for that cell. The
 * result is never `Infinity`.
 */
export function applyComputedColumns(
  rows: readonly TransformRow[],
  config: ComputedColumnsConfig,
): StepApplyResult {
  const warnings: string[] = [];
  const missing: string[] = [];

  for (const column of config.columns) {
    if (column.op !== 'pct_change') {
      warnings.push(
        `computedColumns step skipped: unsupported op '${String(column.op)}'`,
      );
      return { rows: [...rows], warnings };
    }
    if (
      !rowsHaveColumn(rows, column.numerator) &&
      !missing.includes(column.numerator)
    ) {
      missing.push(column.numerator);
    }
    if (
      !rowsHaveColumn(rows, column.denominator) &&
      !missing.includes(column.denominator)
    ) {
      missing.push(column.denominator);
    }
  }

  if (missing.length > 0) {
    return {
      rows: [...rows],
      warnings: [
        `computedColumns step skipped: missing column(s) ${missing.join(', ')}`,
      ],
    };
  }

  const zeroDenominators: string[] = [];
  const nonNumericNumerators: string[] = [];
  const nonNumericDenominators: string[] = [];

  const next = rows.map((row) => {
    const out: TransformRow = { ...row };
    for (const column of config.columns) {
      const numerator = finiteNumber(row[column.numerator]);
      const denominator = finiteNumber(row[column.denominator]);
      if (numerator === undefined || denominator === undefined) {
        out[column.key] = null;
        if (
          numerator === undefined &&
          !nonNumericNumerators.includes(column.numerator)
        ) {
          nonNumericNumerators.push(column.numerator);
        }
        if (
          denominator === undefined &&
          !nonNumericDenominators.includes(column.denominator)
        ) {
          nonNumericDenominators.push(column.denominator);
        }
        continue;
      }
      if (denominator === 0) {
        out[column.key] = null;
        if (!zeroDenominators.includes(column.denominator)) {
          zeroDenominators.push(column.denominator);
        }
        continue;
      }
      const value = (numerator - denominator) / denominator;
      out[column.key] = Number.isFinite(value) ? value : null;
    }
    return out;
  });

  for (const column of nonNumericNumerators) {
    warnings.push(
      `computedColumns wrote null: non-numeric numerator '${column}'`,
    );
  }
  for (const column of nonNumericDenominators) {
    warnings.push(
      `computedColumns wrote null: non-numeric denominator '${column}'`,
    );
  }
  for (const column of zeroDenominators) {
    warnings.push(`computedColumns wrote null: zero denominator '${column}'`);
  }

  return { rows: next, warnings };
}
